import {
  compareAlphanumeric,
  compareBasic,
  toString,
} from "@common/table/sortingFns"
import { compareItems } from "@tanstack/match-sorter-utils"
export const DefaultColumn = {
  filterVariant: "text",
  minSize: 245,
  maxSize: 420,
  size: 245,
  cell: ({ getValue }) => {
    const value = getValue()
    return typeof value == "object" ? value.display : value
  },
  //TODO: global sortingFns can be passed directly to the tableHook instead of adding it to every column
  sortingFn: (rowA, rowB, columnId) => {
    if (rowA.columnFiltersMeta[columnId] && rowB.columnFiltersMeta[columnId])
      return compareItems(
        rowA.columnFiltersMeta[columnId],
        rowB.columnFiltersMeta[columnId]
      )
    const value1 = rowA.getValue(columnId)?.value
    const value2 = rowB.getValue(columnId)?.value
    if (typeof value1 === "number" && typeof value2 === "number")
      return compareBasic(value1, value2)
    return compareAlphanumeric(toString(value1), toString(value2))
  },
}

export const getColumnId = (columnDef) =>
  columnDef.id ?? columnDef.id?.toString?.() ?? columnDef.header

export const getAllLeafColumnDefs = (columns) => {
  const allLeafColumnDefs = []
  const getLeafColumns = (cols) => {
    cols.forEach((col) => {
      if (col?.columns) {
        getLeafColumns(col.columns)
      } else {
        allLeafColumnDefs.push(col)
      }
    })
  }
  getLeafColumns(columns)
  return allLeafColumnDefs
}

export const prepareColumn = ({ columns }) => {
  return columns.map((columnDef) => {
    if (columnDef?.columns?.length) {
      columnDef.type = "group"
      return {
        ...columnDef,
        columns: prepareColumn({ columns: columnDef.columns }),
      }
    } else {
      return {
        ...columnDef,
        accessorFn: (row) => row[columnDef.id],
      }
    }
  })
}

/**
 *
 * @param {any} props
 * @param {Array<string>} grouping
 * @returns {boolean}
 */
export const showExpandColumn = (props, grouping) =>
  !!(
    props.enableExpanding ||
    (props.enableGrouping && (grouping === undefined || grouping?.length))
  )

export const getTotalRight = (table, column) => {
  const right = table
    .getRightLeafHeaders()
    .slice(column.getPinnedIndex() + 1)
    .reduce((acc, col) => acc + col.getSize(), 0)
  return right
}

export const getIsLastLeftPinnedColumn = (table, column) => {
  const lastPinnedColumn = table.getLeftLeafHeaders()?.at(-1)
  return column.getIsPinned() === "left" && lastPinnedColumn?.id === column.id
}

export const getIsFirstRightPinnedColumn = (column) => {
  return column.getIsPinned() === "right" && column.getPinnedIndex() === 0
}

export const getCommonCellStyles = ({ column, table, theme, type = "row" }) => {
  const shadowColor =
    theme === "dark" ? `rgba(248, 250, 252, 0.4)` : `rgba(2, 6, 23, 0.2)`

  const getBoxShadow = () => {
    if (column.id === "row-actions") return
    if (getIsLastLeftPinnedColumn(table, column))
      return `-4px 0 4px -4px ${shadowColor} inset`
    if (getIsFirstRightPinnedColumn(column))
      return `4px 0 4px -4px ${shadowColor} inset`
  }
  // const widthStyles = {
  //   minWidth: `max(calc(var(--${header ? "header" : "col"}-${parseCSSVarId(
  //     header?.id ?? column.id
  //   )}-size) * 1px), ${column.columnDef.minSize ?? 30}px)`,
  //   width: `calc(var(--${header ? "header" : "col"}-${parseCSSVarId(
  //     header?.id ?? column.id
  //   )}-size) * 1px)`,
  // }
  const isVirtual = table.options.enableRowVirtualization
  return {
    // backgroundImage: "inherit",
    boxShadow: getBoxShadow(),
    width: column.getSize(),
    minWidth: column.columnDef.minSize,
    maxWidth: isVirtual ? undefined : column.columnDef.maxSize,
    // !["row-select", "row-expand"].includes(column.id)

    // backgroundColor: column.getIsPinned() ? "var(--background)" : undefined,
    zIndex: column.getIsPinned() ? 1 : undefined,
    left:
      column.getIsPinned() === "left" ? `${column.getStart("left")}px` : undefined,
    opacity:
      table.getState().draggingColumn?.id === column.id ||
      table.getState().hoveredColumn?.id === column.id
        ? 0.5
        : 1,
    position:
      column.getIsPinned() && column.columnDef.type !== "group"
        ? "sticky"
        : undefined,
    right:
      column.getIsPinned() === "right"
        ? `${getTotalRight(table, column)}px`
        : undefined,
    display: isVirtual && type === "row" ? "flex" : undefined,
    flex: isVirtual && type === "row" ? `${column.getSize()} 0 auto` : undefined,
    // ...(table.options.enableColumnResizing && widthStyles),
  }
}

/**
 *
 * @param {} tableOptions
 * @returns { {
 * left: Array<string>,
 * right: Array<string>,
 * }}
 */

export const getDefaultColumnPinning = (tableOptions) => {
  const defaultColumnPinning = {
    left: [],
    right: [],
  }
  if (tableOptions.enableExpanding)
    defaultColumnPinning["left"] = [...defaultColumnPinning.left, "row-expand"]
  if (tableOptions.enableRowSelection)
    defaultColumnPinning["left"] = [...defaultColumnPinning.left, "row-select"]
  if (tableOptions.enableRowActions)
    defaultColumnPinning["right"] = [...defaultColumnPinning.right, "row-actions"]
  return defaultColumnPinning
}

export const getDefaultColumnFilterFn = (columnDef) => {
  if (columnDef.filterVariant === "multi-select") return "arrIncludesSome"
  if (
    columnDef.filterVariant === "range" ||
    columnDef.filterVariant === "range-slider"
  )
    return "betweenInclusive"
  if (columnDef.filterVariant === "select" || columnDef.filterVariant === "checkbox")
    return "equals"
  return "fuzzy"
}

// export const parseCSSVarId = (id) => id.replace(/[^a-zA-Z0-9]/g, "_")
